import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme }        from "@mui/material/styles";

const NavTabs = ({ tabs = [], activeTab, onChange, onBar = {} }) => {
  const theme     = useTheme();
  const t         = theme.custom;
  const scrollRef = useRef(null);

  if (!tabs.length) return null;

  return (
    <Box
      ref={scrollRef}
      sx={{
        display:    "flex",
        alignItems: "center",
        height:     "100%",
        gap:        0,
        // Horizontally scrollable on small screens — no scrollbar visible
        overflowX:              "auto",
        overflowY:              "hidden",
        scrollbarWidth:         "none",
        "&::-webkit-scrollbar": { display: "none" },
        // Fade edges to hint scrollability
        maskImage: "linear-gradient(to right, transparent 0px, black 12px, black calc(100% - 12px), transparent 100%)",
        WebkitMaskImage: "linear-gradient(to right, transparent 0px, black 12px, black calc(100% - 12px), transparent 100%)",
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;

        // ── PILL / CHIP TAB ──
        if (tab.chip) {
          return (
            <Box
              key={tab.id}
              onClick={() => onChange?.(tab.id)}
              sx={{
                display:    "flex",
                alignItems: "center",
                height:     "100%",
                padding:    "0 12px",
                cursor:     "pointer",
                flexShrink: 0,
              }}
            >
              <Box sx={{
                display:      "flex",
                alignItems:   "center",
                gap:          "7px",
                background:   "rgba(40,179,180,0.22)",
                border:       "1px solid rgba(40,179,180,0.45)",
                borderRadius: "20px",
                padding:      "4px 10px 4px 9px",
                transition:   "all 0.18s ease",
                "&:hover":    { background: "rgba(40,179,180,0.32)", borderColor: "rgba(40,179,180,0.65)" },
              }}>
                {tab.chip.dot && (
                  <Box sx={{
                    width:        "7px",
                    height:       "7px",
                    borderRadius: "50%",
                    background:   "#4ade80",
                    flexShrink:   0,
                    boxShadow:    "0 0 5px rgba(74,222,128,0.65)",
                    animation:    "liveDot 2.2s ease-in-out infinite",
                    "@keyframes liveDot": {
                      "0%, 100%": { opacity: 1,    transform: "scale(1)"    },
                      "50%":      { opacity: 0.45, transform: "scale(0.85)" },
                    },
                  }} />
                )}
                <Typography sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize:   "12px",
                  fontWeight: 500,
                  color:      t.accent,
                  whiteSpace: "nowrap",
                  userSelect: "none",
                }}>
                  {tab.chip.label}
                </Typography>
                {tab.chip.badge != null && (
                  <Box sx={{
                    background:     t.accent,
                    color:          "#fff",
                    borderRadius:   "50%",
                    minWidth:       "18px",
                    height:         "18px",
                    display:        "flex",
                    alignItems:     "center",
                    justifyContent: "center",
                    fontFamily:     "'DM Mono', monospace",
                    fontSize:       "9px",
                    fontWeight:     700,
                    flexShrink:     0,
                    padding:        "0 4px",
                  }}>
                    {tab.chip.badge}
                  </Box>
                )}
              </Box>
            </Box>
          );
        }

        // ── PLAIN TAB ──
        return (
          <Box
            key={tab.id}
            onClick={() => onChange?.(tab.id)}
            sx={{
              position:   "relative",
              display:    "flex",
              alignItems: "center",
              gap:        "6px",
              height:     "100%",
              // Tighter padding on small screens
              padding:    { xs: "0 10px", md: "0 16px" },
              cursor:     "pointer",
              flexShrink: 0,
              userSelect: "none",
              "&::after": {
                content:      '""',
                position:     "absolute",
                bottom:       "-1px",
                left:         { xs: "10px", md: "16px" },
                right:        { xs: "10px", md: "16px" },
                height:       "2px",
                background:   isActive ? "rgba(255,255,255,0.7)" : "transparent",
                borderRadius: "2px 2px 0 0",
                transition:   "background 0.18s ease",
              },
              "&:hover": { background: onBar.hover || "rgba(255,255,255,0.06)" },
              "&:hover::after": {
                background: isActive
                  ? "rgba(255,255,255,0.7)"
                  : "rgba(255,255,255,0.2)",
              },
            }}
          >
            <Typography sx={{
              fontFamily: "'Inter', sans-serif",
              // Slightly smaller font on xs screens
              fontSize:   { xs: "11.5px", md: "12.5px" },
              fontWeight: isActive ? 500 : 400,
              color:      isActive
                ? onBar.text  || "rgba(255,255,255,0.90)"
                : onBar.text2 || "rgba(255,255,255,0.55)",
              whiteSpace: "nowrap",
              transition: "color 0.15s ease",
            }}>
              {tab.label}
            </Typography>

            {tab.badge != null && (
              <Box sx={{
                background:   "rgba(255,255,255,0.15)",
                color:        "rgba(255,255,255,0.8)",
                borderRadius: "3px",
                padding:      "1px 5px",
                fontFamily:   "'DM Mono', monospace",
                fontSize:     "9px",
                fontWeight:   600,
              }}>
                {tab.badge}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default NavTabs;